package dev.lingvo.site.components.source

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import dev.lingvo.shared.KeyRow
import dev.lingvo.shared.PluralCategory
import dev.lingvo.shared.ReviewItemDto
import dev.lingvo.shared.SourceChangeDto
import dev.lingvo.shared.SourceSnapshotDto
import dev.lingvo.shared.TranslationDto
import dev.lingvo.shared.validateSourceSnapshot
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LSectionTitle
import dev.lingvo.site.ds.Space
import dev.lingvo.site.components.editor.displayName
import dev.lingvo.site.i18n.t
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

internal fun KeyRow.validSourceSnapshot(): SourceSnapshotDto? = sourceSnapshot?.takeIf {
    validateSourceSnapshot(it) == null && it.view.keyId == key.id &&
        it.view.sourceLocale == source.locale && it.view.isPlural == key.isPlural &&
        it.value == source.value && it.pluralForms == source.pluralForms
}

internal fun ReviewItemDto.validSourceSnapshot(): SourceSnapshotDto? = sourceSnapshot?.takeIf {
    validateSourceSnapshot(it) == null && it.view.keyId == keyId &&
        it.value == sourceValue && it.pluralForms == sourcePluralForms
}

internal fun SourceChangeDto.hasIdentity(keyId: String, locale: String): Boolean =
    this.keyId == keyId && this.locale == locale &&
        currentSource?.let { it.view.keyId == keyId } != false &&
        target?.let { it.keyId == keyId && it.locale == locale } != false

internal fun SourceChangeDto.validCurrentSource(): SourceSnapshotDto? = currentSource?.takeIf {
    validateSourceSnapshot(it) == null && it.view.keyId == keyId
}

/** The earlier value belongs to the returned saved target, never to a cached browser draft. */
internal fun SourceChangeDto.validEarlierSource(): SourceSnapshotDto? = earlierSource?.takeIf {
    validateSourceSnapshot(it) == null && it.view.keyId == keyId &&
        target?.sourceRevisionId == it.view.sourceRevisionId
}

@Composable
internal fun SourceComparison(
    current: SourceSnapshotDto?,
    earlier: SourceSnapshotDto?,
    savedTarget: TranslationDto?,
    draftBasis: SourceSnapshotDto? = null,
    showDraftBasis: Boolean = false,
) {
    Column(Modifier.fillMaxWidth().gap(Space.MD).attr("data-source-comparison", "true")) {
        SourceSnapshotPanel(t("source.source_review.current_source", "Current source"), current, "current")
        if (showDraftBasis) {
            SourceSnapshotPanel(
                t("source.source_review.source_when_this_draft_started", "Source when this draft started"), draftBasis, "draft",
                t("source.source_review.draft_source_unavailable", "Draft source unavailable"),
            )
        }
        SourceSnapshotPanel(
            t("source.source_review.earlier_source", "Earlier source"), earlier, "earlier",
            t("source.source_review.earlier_source_unavailable", "Earlier source unavailable"),
        )
        if (earlier == null) LMuted(t(
            "source.source_review.the_recorded_basis_is_unknown_translation",
            "The recorded basis is unknown. Translation history does not establish which source was used.",
        ))
        savedTarget?.let { target ->
            LCard(Modifier.fillMaxWidth().attr("data-source-saved-target", "true")) {
                Column(Modifier.gap(Space.SM)) {
                    LSectionTitle(t("source.source_review.saved_translation", "Saved translation"))
                    SourceValues(target.locale, target.value, target.pluralForms)
                }
            }
        }
    }
}

@Composable
internal fun SourceSnapshotPanel(
    label: String,
    snapshot: SourceSnapshotDto?,
    hook: String,
    unknown: String = t("source.source_review.current_source_revision_unavailable", "Current source revision unavailable"),
) {
    LCard(Modifier.fillMaxWidth().attr("data-source-snapshot", hook).attr("role", "group").attr("aria-label", label)) {
        Column(Modifier.gap(Space.SM)) {
            LSectionTitle(label)
            if (snapshot == null) LMuted(unknown) else SourceValues(snapshot.view.sourceLocale, snapshot.value, snapshot.pluralForms)
        }
    }
}

@Composable
internal fun RetainedTargetPanel(label: String, locale: String, value: String?, forms: Map<PluralCategory, String>?) {
    LCard(Modifier.fillMaxWidth().attr("data-source-snapshot", "draft-target").attr("role", "group").attr("aria-label", label)) {
        Column(Modifier.gap(Space.SM)) {
            LSectionTitle(label)
            if (value != null && forms != null) {
                LMuted(t("source.source_review.scalar_text", "Scalar text"))
                SourceText(locale, value)
                LMuted(t("source.source_review.plural_forms", "Plural forms"))
                SourceValues(locale, null, forms)
            } else SourceValues(locale, value, forms)
        }
    }
}

@Composable
private fun SourceValues(locale: String, value: String?, forms: Map<PluralCategory, String>?) {
    when {
        forms != null && forms.isNotEmpty() -> PluralCategory.entries.forEach { category ->
            forms[category]?.let { form ->
                Column(Modifier.gap(Space.XS).attr("data-source-form", category.name.lowercase())) {
                    LMuted(category.displayName())
                    SourceText(locale, form)
                }
            }
        }
        value != null -> SourceText(locale, value)
        else -> LMuted(t("source.source_review.no_text_is_recorded_for_this", "No text is recorded for this version."))
    }
}

@Composable
private fun SourceText(locale: String, value: String) {
    Div(Modifier.fillMaxWidth().styleModifier {
        property("white-space", "break-spaces")
        property("overflow-wrap", "anywhere")
        property("unicode-bidi", "plaintext")
        property("user-select", "text")
    }.toAttrs {
        attr("data-source-text", "true")
        attr("lang", locale)
        attr("dir", "auto")
    }) { Text(value) }
}
